import { WEBSITE_URL_REGEX } from '@mondaygpt/constants'
import { Button, PinterestIcon } from 'components'
import { useToast } from 'hooks'
import { cn } from 'lib/utils'
import {
  AlertCircle,
  Check,
  Facebook,
  Globe,
  Instagram,
  Linkedin,
  Loader2,
  MapPin,
  Music2,
  Sparkle as SparkleIcon,
} from 'lucide-react'
import type { ComponentType } from 'react'
import { useDebouncedCheck } from 'modules/common/hooks/use-debounced-check'
import { cleanUrl, extractSocialHandle } from 'modules/common/utils/domain-validation'
import {
  checkUrl,
  findSocialsOnWebsite,
  type FindSocialsResponse,
} from 'modules/creative/api/brand-kit-api'
import { useEffect, useRef, useState } from 'react'
import { Eyebrow } from './Eyebrow'
import { MobileStickyFooter } from './MobileStickyFooter'
import { FlowChrome } from './flow-chrome'
import type { ScrapeInput } from './use-scan-stream'

// ─── Magic-reveal timing ────────────────────────────────────────────────────
// Cross-file relationship: SPARKLE_HOLD_MS must outlive the .sparkle CSS
// animation duration in design-system.css (currently 0.9s) so the row's
// `sparkle` flag stays true until the twinkle completes.
const TYPE_CHAR_MS = 35
const TYPE_BETWEEN_VALUES_MS = 140
const SPARKLE_HOLD_MS = 1100

// Static map of (PresenceData key, FindSocialsResponse field) so the
// auto-fill effect can iterate instead of repeating the same `if (!data.X
// && socials.Y)` block per row.
const AUTO_FILL_FIELDS = [
  ['instagramHandle', 'instagram'],
  ['facebookHandle', 'facebook'],
  ['linkedinUrl', 'linkedin'],
  ['tiktokHandle', 'tiktok'],
  ['pinterestHandle', 'pinterest'],
] as const satisfies ReadonlyArray<readonly [keyof PresenceData, keyof FindSocialsResponse]>

/**
 * The user's online presence — the entry point of the new onboarding.
 *
 * The flow:
 *   1. User types a website URL → we HEAD-check it (✓/!)
 *   2. We also fetch the website's HTML and pull the social handles it links
 *      to in the footer/anchors. Those auto-fill into any empty social rows
 *      that match — the user doesn't have to retype handles their site
 *      already declares.
 *   3. Every filled row HEAD-checks its constructed URL — ✓ if the platform
 *      responds, ! if not.
 *   4. Empty rows show no indicator. Continue is enabled when ≥1 row is filled.
 *   5. Skip jumps straight to the brand card with whatever was filled.
 */
export interface PresenceData {
  websiteUrl: string
  instagramHandle: string
  facebookHandle: string
  linkedinUrl: string
  tiktokHandle: string
  pinterestHandle: string
  googleBusinessUrl: string
}

export const EMPTY_PRESENCE: PresenceData = {
  websiteUrl: '',
  instagramHandle: '',
  facebookHandle: '',
  linkedinUrl: '',
  tiktokHandle: '',
  pinterestHandle: '',
  googleBusinessUrl: '',
}

/** Provenance of a single presence field at submit time. */
export type PresenceFieldSource = 'manual' | 'autofilled' | 'empty'

/**
 * Computed the moment the user leaves the presence step (Continue or Skip).
 * Carries the two signals only PresenceStep can see — per-field provenance (it
 * owns the `autoFilled` set) and website format validity — up to the parent so
 * DarkOnboardingFlow can both decide whether to scan and fire the unified
 * `onboarding_presence_submitted` event.
 */
export interface PresenceSubmitMeta {
  /** Website passed WEBSITE_URL_REGEX (format only; false when empty). */
  websiteValid: boolean
  /** Per-field provenance for every PresenceData key. Website is never auto-filled. */
  sources: Record<keyof PresenceData, PresenceFieldSource>
  /**
   * Whether there's anything worth scanning: any social handle present, or a
   * format-valid website. Only a website-alone-and-invalid (or fully empty)
   * submission is non-scrapeable.
   */
  scrapeable: boolean
}

interface PresenceStepProps {
  chrome: FlowChrome
  initial: PresenceData
  onSubmit: (data: PresenceData, scrapeInput: ScrapeInput, meta: PresenceSubmitMeta) => void
  onSkip: (data: PresenceData, meta: PresenceSubmitMeta) => void
}

interface ChannelDef {
  key: keyof PresenceData
  label: string
  icon: ComponentType<{ className?: string }>
  prefix?: string
  placeholder: string
  /**
   * Build the canonical URL for this row's value. Used for HEAD reachability
   * (the verification check) AND for the click-to-open link. Returns '' when
   * the input doesn't form something we'd link to.
   */
  buildUrl: (value: string) => string
}

/**
 * Display labels for each PresenceData field. Single source of truth so the
 * paywall personalization (DarkPaywallStep) and any other surface that wants
 * to name the user's channels stays consistent with the row labels here.
 */
export const PRESENCE_FIELD_LABELS = {
  websiteUrl: 'Website',
  instagramHandle: 'Instagram',
  facebookHandle: 'Facebook',
  linkedinUrl: 'LinkedIn',
  tiktokHandle: 'TikTok',
  pinterestHandle: 'Pinterest',
  googleBusinessUrl: 'Google Business',
} as const satisfies Record<keyof PresenceData, string>

const UNIVERSAL_CHANNELS: ChannelDef[] = [
  {
    key: 'websiteUrl',
    label: PRESENCE_FIELD_LABELS.websiteUrl,
    icon: Globe,
    placeholder: 'yourbusiness.com',
    buildUrl: v => (v.trim() ? `https://${cleanUrl(v)}` : ''),
  },
  {
    key: 'instagramHandle',
    label: PRESENCE_FIELD_LABELS.instagramHandle,
    icon: Instagram,
    prefix: '@',
    placeholder: 'yourbusiness',
    buildUrl: v => {
      const handle = extractSocialHandle(v, 'instagram')
      return handle ? `https://instagram.com/${handle}` : ''
    },
  },
  {
    key: 'facebookHandle',
    label: PRESENCE_FIELD_LABELS.facebookHandle,
    icon: Facebook,
    prefix: 'fb.com/',
    placeholder: 'yourbusiness',
    buildUrl: v => {
      const handle = extractSocialHandle(v, 'facebook')
      return handle ? `https://facebook.com/${handle}` : ''
    },
  },
  {
    key: 'linkedinUrl',
    label: PRESENCE_FIELD_LABELS.linkedinUrl,
    icon: Linkedin,
    prefix: 'linkedin.com/',
    placeholder: 'company/yourbusiness',
    buildUrl: v => {
      const path = cleanLinkedinPath(v)
      return path ? `https://linkedin.com/${path}` : ''
    },
  },
  {
    key: 'tiktokHandle',
    label: PRESENCE_FIELD_LABELS.tiktokHandle,
    icon: Music2,
    prefix: 'tiktok.com/',
    placeholder: '@yourbusiness',
    buildUrl: v => {
      const handle = cleanTiktokHandle(v)
      return handle ? `https://tiktok.com/@${handle}` : ''
    },
  },
  {
    key: 'pinterestHandle',
    label: PRESENCE_FIELD_LABELS.pinterestHandle,
    icon: PinterestIcon,
    prefix: 'pinterest.com/',
    placeholder: 'yourbusiness',
    buildUrl: v => {
      const handle = cleanPinterestHandle(v)
      return handle ? `https://pinterest.com/${handle}` : ''
    },
  },
]

const REPUTATION_CHANNEL: ChannelDef = {
  key: 'googleBusinessUrl',
  label: PRESENCE_FIELD_LABELS.googleBusinessUrl,
  icon: MapPin,
  placeholder: 'g.page/yourbusiness',
  buildUrl: v => (v.trim() ? `https://${cleanUrl(v)}` : ''),
}

export const PresenceStep: React.FC<PresenceStepProps> = ({
  chrome,
  initial,
  onSubmit,
  onSkip,
}) => {
  const [data, setData] = useState<PresenceData>(initial)
  const { addToast } = useToast()

  useEffect(() => {
    setData(initial)
  }, [initial])

  const channels = chrome.showGoogleBusinessField
    ? [REPUTATION_CHANNEL, ...UNIVERSAL_CHANNELS]
    : UNIVERSAL_CHANNELS

  const setField = (key: keyof PresenceData, value: string) =>
    setData(prev => ({ ...prev, [key]: value }))

  // Auto-fill: when the website scan returns, stream the discovered handles
  // into any empty social rows character-by-character. Tracked via ref so we
  // apply each scan result at most once — if the user clears a field, we
  // don't re-fill it.
  const websiteSocials = useWebsiteSocials(data.websiteUrl)
  const lastAutoFillRef = useRef<typeof websiteSocials.data>(undefined)
  // `autoFilled` is sticky (until the user edits the field) and lets the row
  // skip the HEAD check; `justFinishedTyping` is a per-row transient that
  // fires the sparkle for SPARKLE_HOLD_MS then clears.
  const [autoFilled, setAutoFilled] = useState<Set<keyof PresenceData>>(new Set())
  const [justFinishedTyping, setJustFinishedTyping] = useState<Set<keyof PresenceData>>(new Set())
  useEffect(() => {
    const socials = websiteSocials.data
    if (!socials || socials === lastAutoFillRef.current) return
    lastAutoFillRef.current = socials

    const queue = AUTO_FILL_FIELDS.flatMap(([key, socialKey]) => {
      const discovered = socials[socialKey]
      return !data[key] && discovered ? [{ key, value: discovered }] : []
    })
    if (queue.length === 0) return

    // Mark auto-verified up front so the ✓ shows during typing instead of
    // cycling through HEAD-check states on each partial value.
    setAutoFilled(prev => new Set([...prev, ...queue.map(q => q.key)]))

    const timeouts: ReturnType<typeof setTimeout>[] = []
    const cancelStream = streamCharacters(queue, setData, key => {
      setJustFinishedTyping(curr => new Set(curr).add(key))
      timeouts.push(
        setTimeout(
          () =>
            setJustFinishedTyping(curr => {
              const next = new Set(curr)
              next.delete(key)
              return next
            }),
          SPARKLE_HOLD_MS
        )
      )
    })
    return () => {
      cancelStream()
      timeouts.forEach(clearTimeout)
    }
    // We deliberately omit `data` from deps — auto-fill only runs when the
    // scan result lands. Re-running on every keystroke would cause loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteSocials.data])

  const handleFieldChange = (key: keyof PresenceData, value: string) => {
    setField(key, value)
    // The user has taken ownership of this field — drop it from the auto-fill
    // set so the row goes back to a normal HEAD-verified path.
    if (autoFilled.has(key)) {
      setAutoFilled(prev => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }
  }

  const filledCount = channels.reduce((n, c) => n + (data[c.key].trim() ? 1 : 0), 0)
  const totalCount = channels.length
  const websiteFilled = data.websiteUrl.trim() !== ''
  const websiteFormatValid = !websiteFilled || WEBSITE_URL_REGEX.test(cleanUrl(data.websiteUrl))
  // Junk like "asdf" as the ONLY input used to sail through Continue and mint
  // a brand card literally named "Asdf". A format-invalid website alone can't
  // continue; with a social handle present it can (the handle drives the scan)
  // and the inline note still nudges the fix. Skip stays available regardless.
  const onlyInvalidWebsite = websiteFilled && !websiteFormatValid && filledCount === 1
  const canContinue = filledCount > 0 && !onlyInvalidWebsite

  // Snapshot the per-field provenance + website validity + scrapeable decision
  // at the moment the user leaves the step. `autoFilled` and the format check
  // live here; the parent gets the distilled result via onSubmit/onSkip.
  const buildMeta = (): PresenceSubmitMeta => {
    const keys = Object.keys(data) as (keyof PresenceData)[]
    const sources = Object.fromEntries(
      keys.map(key => {
        const source: PresenceFieldSource = !data[key].trim()
          ? 'empty'
          : autoFilled.has(key)
            ? 'autofilled'
            : 'manual'
        return [key, source]
      })
    ) as Record<keyof PresenceData, PresenceFieldSource>

    const websiteValid =
      data.websiteUrl.trim() !== '' && WEBSITE_URL_REGEX.test(cleanUrl(data.websiteUrl))

    // Any non-website field counts as scrapeable intent — even handles the
    // scraper currently drops (product decision: only website-alone-invalid or
    // empty skips the scan).
    const anyHandlePresent = keys.some(key => key !== 'websiteUrl' && sources[key] !== 'empty')

    return { websiteValid, sources, scrapeable: anyHandlePresent || websiteValid }
  }

  const handleContinue = () => {
    if (!canContinue) return
    onSubmit(data, toScrapeInput(data), buildMeta())
  }

  const handleGoogleConnect = () => {
    addToast(
      'info',
      'Google Business OAuth is coming soon. For now, paste your Google Business URL above.',
      4500
    )
  }

  return (
    <div className="mx-auto flex max-w-[640px] flex-col gap-7">
      <div className="flex flex-col gap-3">
        <Eyebrow>{chrome.presenceEyebrow}</Eyebrow>
        <h2 className="text-[32px] leading-[1.05] font-semibold tracking-[-0.02em] text-paper sm:text-[40px]">
          Let's get to know you.
        </h2>
        <p className="lede !text-[15px] !leading-[1.55]">
          So every post we create sounds like yours, not a template.
        </p>
      </div>

      <div className="card flex flex-col gap-1 p-2 sm:p-3">
        {channels.map((channel, i) => (
          <ChannelRow
            key={channel.key}
            channel={channel}
            value={data[channel.key]}
            onChange={value => handleFieldChange(channel.key, value)}
            isLast={i === channels.length - 1}
            autoVerified={autoFilled.has(channel.key)}
            sparkle={justFinishedTyping.has(channel.key)}
          />
        ))}
      </div>

      {websiteFilled && !websiteFormatValid && (
        <p className="text-[13px] text-[color:var(--alert-error-text)]">
          That doesn't look like a website address (like yourbusiness.com).
          {onlyInvalidWebsite ? ' Fix it or add a social handle to continue.' : ''}
        </p>
      )}

      <MobileStickyFooter>
        <Button
          variant="t3-primary"
          size="t3-lg"
          onClick={handleContinue}
          disabled={!canContinue}
          className="!max-w-[420px]"
          data-testid="onboarding-presence-submit"
        >
          Continue →
        </Button>

        {chrome.showGoogleBusinessConnect && (
          <Button
            type="button"
            variant="t3-tertiary"
            size="cta"
            onClick={handleGoogleConnect}
            className="!h-[44px] !max-w-[420px]"
          >
            Sign in with Google Business
          </Button>
        )}

        <div className="flex items-center gap-3 text-[12px]">
          <span className="text-paper-faint">
            {filledCount} of {totalCount} added
          </span>
          <span className="text-paper-faint">·</span>
          <button type="button" onClick={() => onSkip(data, buildMeta())} className="link-muted">
            I'll set it up later
          </button>
        </div>
      </MobileStickyFooter>
    </div>
  )
}

const ChannelRow: React.FC<{
  channel: ChannelDef
  value: string
  onChange: (next: string) => void
  isLast: boolean
  /** Pre-verified by the website scrape — skip the HEAD check, mark ✓ directly. */
  autoVerified: boolean
  /** Just finished typewriter-typing — twinkle a small sparkle on the value. */
  sparkle: boolean
}> = ({ channel, value, onChange, isLast, autoVerified, sparkle }) => {
  const Icon = channel.icon
  const url = channel.buildUrl(value)
  // Skip the HEAD check for auto-verified rows so we don't burn rate limit on
  // values we already trust — the website itself listed them.
  const checkStatus = useReachabilityCheck(autoVerified ? '' : url)
  const status: CheckStatus = autoVerified && url ? 'success' : checkStatus

  return (
    <label
      className={cn(
        'group flex items-center gap-3 rounded-[8px] px-3 py-2.5 transition-colors',
        !isLast && 'border-b border-line'
      )}
    >
      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[8px] border border-line-bright bg-bg-3 text-paper-dim transition-colors group-focus-within:border-acid group-focus-within:text-acid group-hover:text-paper">
        <Icon className="h-4 w-4" />
      </span>
      {/* Label is hidden on mobile so the placeholder/value column has room
          to breathe; the unique icon (globe / IG / FB / IN) carries the
          channel context at narrow widths. Returns at sm+. */}
      <span className="hidden w-[110px] flex-shrink-0 text-[13px] font-medium text-paper sm:inline">
        {channel.label}
      </span>
      <span className="flex flex-1 items-center">
        {channel.prefix && !value.includes(channel.prefix) && (
          <span className="mono hidden text-[12px] tracking-[0.02em] text-paper-faint sm:inline">
            {channel.prefix}
          </span>
        )}
        <span className="relative min-w-0 flex-1">
          <input
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={channel.placeholder}
            className="w-full bg-transparent text-[14px] text-paper outline-none placeholder:text-paper-faint"
            autoComplete="off"
            spellCheck={false}
          />
          {sparkle && (
            <span
              className="pointer-events-none absolute inset-y-0 left-0 flex items-center text-[14px]"
              aria-hidden
            >
              {/* Invisible mirror of the value anchors the sparkle to the
                  actual end of typed text, regardless of value length. */}
              <span className="invisible">{value}</span>
              <SparkleIcon className="sparkle ml-1.5 h-3.5 w-3.5" strokeWidth={1.8} />
            </span>
          )}
        </span>
        <RowIndicator status={status} url={url} />
      </span>
    </label>
  )
}

/**
 * Stream a queue of (key, value) pairs into PresenceData character by
 * character — like the system is typing them in. Each value's chars at
 * `charDelayMs` apart, with a `betweenValuesMs` gap between consecutive
 * values. Calls `onValueDone(key)` after each value finishes typing.
 * Returns a cleanup function that cancels any pending timers.
 */
function streamCharacters(
  queue: Array<{ key: keyof PresenceData; value: string }>,
  setState: React.Dispatch<React.SetStateAction<PresenceData>>,
  onValueDone: (key: keyof PresenceData) => void
): () => void {
  const timeouts: ReturnType<typeof setTimeout>[] = []
  let cumulative = 0
  for (const { key, value } of queue) {
    for (let i = 1; i <= value.length; i++) {
      const partial = value.slice(0, i)
      timeouts.push(
        setTimeout(
          () => setState(prev => ({ ...prev, [key]: partial })),
          cumulative + i * TYPE_CHAR_MS
        )
      )
    }
    cumulative += value.length * TYPE_CHAR_MS
    timeouts.push(setTimeout(() => onValueDone(key), cumulative))
    cumulative += TYPE_BETWEEN_VALUES_MS
  }
  return () => timeouts.forEach(clearTimeout)
}

// ─── Verification ───────────────────────────────────────────────────────────

type CheckStatus = 'idle' | 'checking' | 'success' | 'error'

/**
 * HEAD-check the constructed URL for a row. Lenient: any HTTP response from
 * the platform counts as "valid" (✓), since social platforms always respond
 * to a request even when they redirect to a login wall — the response itself
 * is proof the URL is shaped correctly.
 *
 * The format pre-check throws client-side for inputs that don't pass the
 * website regex, which keeps garbage input ("not a url") from round-tripping
 * the server for a 400 on every keystroke.
 */
function useReachabilityCheck(url: string): CheckStatus {
  const { status } = useDebouncedCheck({
    value: url,
    clean: v => (v ? cleanUrl(v) : ''),
    check: async cleaned => {
      if (!WEBSITE_URL_REGEX.test(cleaned)) throw new Error('invalid-format')
      return checkUrl(cleaned)
    },
    isSuccess: r => r.reachable,
    debounceMs: 700,
  })
  return status
}

/**
 * Pull the social handles the user's website itself declares. Used to
 * auto-fill the social rows so the user doesn't have to retype what their
 * site already publishes.
 *
 * 1.5s debounce because the request fetches the full HTML of the user's site;
 * we don't want to spam it on every keystroke.
 */
function useWebsiteSocials(websiteUrl: string): {
  status: CheckStatus
  data: FindSocialsResponse | undefined
} {
  const { status, data } = useDebouncedCheck({
    value: websiteUrl,
    clean: v => (v.trim() ? cleanUrl(v) : ''),
    check: async cleaned => {
      if (!WEBSITE_URL_REGEX.test(cleaned)) throw new Error('invalid-format')
      return findSocialsOnWebsite(cleaned)
    },
    isSuccess: () => true,
    debounceMs: 1500,
  })
  return { status, data }
}

const RowIndicator: React.FC<{ status: CheckStatus; url: string }> = ({ status, url }) => {
  if (status === 'idle') {
    return <span className="field-status" aria-hidden />
  }
  if (status === 'checking') {
    return (
      <span className="field-status" data-state="checking">
        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} aria-label="Checking" />
      </span>
    )
  }
  if (status === 'success') {
    // External-link affordance so the user can one-click verify visually.
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={e => e.stopPropagation()}
        className="field-status transition-colors hover:!text-paper"
        data-state="success"
        title={`Open ${url} in a new tab`}
        aria-label="Reachable, open in a new tab"
      >
        <Check className="h-4 w-4" strokeWidth={2.4} />
      </a>
    )
  }
  return (
    <span className="field-status" data-state="error" title="We couldn't reach this URL">
      <AlertCircle className="h-4 w-4" strokeWidth={2} aria-label="Couldn't reach" />
    </span>
  )
}

/**
 * Strip protocol, www, and the linkedin.com host from whatever the user typed,
 * leaving the path. `company/X`, `in/X`, full URLs, and bare slugs all collapse
 * to the canonical path; empty input returns ''.
 */
function cleanLinkedinPath(value: string): string {
  return value
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/^linkedin\.com\/+/i, '')
    .replace(/^\/+/, '')
    .replace(/\/+$/, '')
}

/** Reduce a TikTok URL/handle/@handle to the bare username (no leading @). */
function cleanTiktokHandle(value: string): string {
  return value
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/^tiktok\.com\/+/i, '')
    .replace(/^@/, '')
    .split(/[/?#]/)[0]
    .replace(/^@/, '')
}

/** Reduce a Pinterest URL/handle to the bare username (first path segment). */
function cleanPinterestHandle(value: string): string {
  return value
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/^pinterest\.[a-z.]+\/+/i, '')
    .replace(/^\/+/, '')
    .split(/[?#]/)[0]
    .replace(/\/+$/, '')
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Convert the Presence form to the scrape orchestrator's input shape. Strip
 * the protocol off the website (`cleanUrl`) and the @ off the handle
 * (`extractSocialHandle`) so the validators on the server pass.
 */
function toScrapeInput(data: PresenceData): ScrapeInput {
  const websiteUrl = data.websiteUrl.trim() ? cleanUrl(data.websiteUrl) : undefined
  const instagramHandle = data.instagramHandle.trim()
    ? extractSocialHandle(data.instagramHandle, 'instagram')
    : undefined
  const facebookHandle = data.facebookHandle.trim()
    ? extractSocialHandle(data.facebookHandle, 'facebook')
    : undefined
  return {
    websiteUrl: websiteUrl || undefined,
    instagramHandle: instagramHandle || undefined,
    facebookHandle: facebookHandle || undefined,
  }
}
